import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Card, Loader, Svg, Typography, useThemeValue } from '@altermaind/web/ui';
import { AlertBadge } from '@/_shared/components/AlertBadge';
import { Routes } from '@/_shared/constants/routes';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { getDepartmentTeamMembersCall } from '@/departments/state';
import { getEmployeesMeCall } from '@/profile/state';

export const TeamList = () => {
  const tShared = useTranslations();
  const navigate = useNavigate();
  const { leadership } = getEmployeesMeCall.hook();
  const [hoveredEmployeeId, setHoveredEmployeeId] = useState<string | null>(null);

  const gapXs = useThemeValue('space', 'xs');
  const { genericResponsiveValue } = useResponsiveStyle();

  const {
    execute: executeGetDepartmentTeamMembers,
    loading: loadingGetDepartmentTeamMembers,
    error: errorGetDepartmentTeamMembers,
    response: responseGetDepartmentTeamMembers,
  } = getDepartmentTeamMembersCall.hook();

  const goToEmployeeProfile = (employeeId: string) => {
    navigate(Routes.TeamEmployeeDetails.replace(':employeeId', employeeId));
  };

  useEffect(() => {
    executeGetDepartmentTeamMembers({
      departmentId: leadership?.team?.departmentId || '',
      teamId: leadership?.team?.id || '',
      excludeMemberType: ['teamManager'],
    });
  }, [executeGetDepartmentTeamMembers, leadership]);

  if (loadingGetDepartmentTeamMembers) {
    return (
      <Box mt='s' textAlign='center'>
        <Loader size='s' />
      </Box>
    );
  }

  if (errorGetDepartmentTeamMembers) {
    const message = tShared('errors.genericApiError');
    return <AlertBadge containerProps={{ mt: 's' }} iconName='info' type='error' label={message} />;
  }

  if (responseGetDepartmentTeamMembers && (responseGetDepartmentTeamMembers?.totalItems ?? 0) > 0) {
    return (
      <Box mt='l'>
        <Box alignItems='center' display='flex' flexDirection='row' mt='xxxs'>
          <Typography mr='s' variant='h3'>
            {leadership?.team?.name}
          </Typography>
          <Box alignItems='center' flexDirection='row' display='flex'>
            <Svg name='users' size='xs' />
            <Typography ml='xxs' variant='h3'>
              {responseGetDepartmentTeamMembers?.totalItems || 0}
            </Typography>
          </Box>
        </Box>
        <Box>
          <Box
            display='grid'
            gridTemplateColumns={genericResponsiveValue({
              breakPoints: {
                desktop: 'repeat(3, 1fr)',
                tablet: 'repeat(1, 1fr)',
                mobile: 'repeat(1, 1fr)',
              },
              defaultValue: 'repeat(1, 1fr)',
            })}
            style={{ columnGap: gapXs }}
          >
            {(responseGetDepartmentTeamMembers?.data ?? []).map(employee => (
              <Box
                key={employee.id}
                onMouseEnter={() => setHoveredEmployeeId(employee.id)}
                onMouseLeave={() => setHoveredEmployeeId(null)}
              >
                <Card
                  style={{ cursor: 'pointer' }}
                  p='s'
                  mt='xs'
                  backgroundColor='grey/dark/background'
                  onClick={() => goToEmployeeProfile(employee.id)}
                >
                  <Box display='flex' alignItems='center' justifyContent='space-between'>
                    <Box alignItems='center' display='flex' flexDirection='row'>
                      <Box
                        backgroundColor='primary'
                        borderRadius='xl'
                        alignItems='center'
                        justifyContent='center'
                        width='xxl'
                        height='xxl'
                        display='flex'
                        flexDirection='row'
                      >
                        <Typography
                          display='inline-flex'
                          justifyContent='center'
                          alignItems='center'
                          variant='h4'
                        >
                          {`${employee.firstName?.charAt(0) ?? ''}${employee.lastName?.charAt(0) ?? ''}`}
                        </Typography>
                      </Box>
                      <Box display='flex' flexDirection='column'>
                        <Typography ml='xs' variant='h4'>
                          {`${employee.firstName} ${employee.lastName}`}
                        </Typography>
                        <Typography ml='xs' fontSize='xs' variant='p3/light'>
                          {employee.role || ''}
                        </Typography>
                      </Box>
                    </Box>
                    {hoveredEmployeeId === employee.id && <Svg name='arrowRight' size='s' />}
                  </Box>
                </Card>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    );
  }

  return null;
};
